import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { InvoiceFee } from '../../../../core/models/invoice-settings/invoice-settings.model';
import { MapperInvoiceSettingsService } from '../../../../core/mappers/invoice-settings/mapper-invoice-settings.service';
import { ApiInvoiceSettingsService } from '../../../../core/services/api/invoice-settings/api-invoice-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalInvoiceFeeComponent } from '../../modals/system-settings/modal-invoice-fee/modal-invoice-fee.component';

@Component({
  selector: 'app-setting-invoice-fee',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoiceFeeComponent],
  templateUrl: './setting-invoice-fee.component.html',
  styleUrl: './setting-invoice-fee.component.css',
})
export class SettingInvoiceFeeComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiInvoiceSettingsService);
  private readonly mapper = inject(MapperInvoiceSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  fees = signal<InvoiceFee[]>([]);
  isLoading = signal(false);
  searchText = signal('');
  showInactive = signal(true);
  isModalOpen = signal(false);
  selectedFee = signal<InvoiceFee | null>(null);
  dashboardStats = signal<Array<{ title: string; value: string; icon: string; color: string }>>([]);
  isDashboardOpen = signal(true);

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];
  Math = Math;

  filteredData = computed(() => {
    let rows = this.fees();
    const q = this.searchText().trim().toLowerCase();
    if (!this.showInactive()) {
      rows = rows.filter((r) => r.status);
    }
    if (q) {
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.feeName.toLowerCase().includes(q) ||
          r.feeTypeName.toLowerCase().includes(q)
      );
    }
    return [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  });

  totalItems = computed(() => this.filteredData().length);
  filteredAndPaginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredData().slice(start, start + this.pageSize());
  });
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadFees();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) {
      this.loadFees();
    }
  }

  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  onRefresh(): void {
    this.searchText.set('');
    this.currentPage.set(1);
    this.loadFees();
  }

  onSearch(): void {
    this.currentPage.set(1);
  }

  navigateToCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedFee.set(null);
    this.isModalOpen.set(true);
  }

  navigateToEdit(item: InvoiceFee): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedFee.set(item);
    this.isModalOpen.set(true);
  }

  deactivate(item: InvoiceFee): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ/ปิดใช้งาน');
      return;
    }
    if (!confirm(`ปิดใช้งานรายการ "${item.name}" ?`)) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }
    const payload = this.mapper.mapDeactivatePayload(userId, menuId);

    this.api.deactivateInvoiceFee(item.id, payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'ปิดใช้งานสำเร็จ');
          this.loadFees();
        } else {
          this.toast.error(mapped.message || 'ปิดใช้งานไม่สำเร็จ');
        }
      },
      error: () => this.toast.error('เกิดข้อผิดพลาด'),
    });
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedFee.set(null);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  onPageSizeChange(event: Event): void {
    this.pageSize.set(Number((event.target as HTMLSelectElement).value));
    this.currentPage.set(1);
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadFees(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.fees.set([]);
      return;
    }

    this.isLoading.set(true);
    this.api.getInvoiceFeeList(siteId, false).subscribe({
      next: (res) => {
        this.fees.set(this.mapper.mapFeeList(res));
        this.calculateDashboardStats();
        this.isLoading.set(false);
      },
      error: () => {
        this.fees.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลค่าธรรมเนียมล้มเหลว');
      },
    });
  }

  private calculateDashboardStats(): void {
    const rows = this.fees();
    const active = rows.filter((r) => r.status).length;
    this.dashboardStats.set([
      { title: 'รายการทั้งหมด', value: rows.length.toLocaleString(), icon: 'payments', color: 'text-amber-500' },
      { title: 'เปิดใช้งาน', value: active.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
      { title: 'ปิดใช้งาน', value: (rows.length - active).toLocaleString(), icon: 'cancel', color: 'text-rose-500' },
    ]);
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
